/**
 * The receipt history of a purchase order (batch 8 T12c2, #3033): server-paged receipts with their lines, hold decisions, names and
 * quality provenance, and the append-only close-short audit.
 *
 *  - Paging is the server's: the count shown is the true total, the rows are the rows of the page, and a page past the end says so
 *    instead of pretending the history is empty.
 *  - A line without a recorded quality check (legacy receipts) shows "no check recorded" and no rejected/held figures: it is not a
 *    checked zero.
 *  - Costs (order line price, discount, cost layers) are shown only to a reader who holds `reports.view_financial` NOW, and exactly as
 *    the server sent them. A reader without the grant sees units and provenance only, also right after a downgrade.
 *  - The close-short audit lists at most the latest 50 events; the panel says so when the true total is larger.
 *
 * Standalone: it takes the purchase order id (and optionally the lines, to name them) and enforces the owner/target boundary itself.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, AlertTitle, Box, Button, Chip, CircularProgress, Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { useNotify } from '@/hooks/useNotify';
import { formatCentsForDisplay } from '@/utils/money';
import { useReceiptHistory, useReceivingOperations, type SubmitResult } from '@/hooks/usePurchaseReceipts';
import { describeReceivingError } from './receivingErrors';
import type { JournalEntry } from './receivingJournal';
import { accessKey, isSessionCancel, readReceivingAccess, useReceivingAccess } from './receivingSession';
import { centsTextToEuros, formatDateTime } from './receivingFormat';
import { blockedMessage, describeEntry, JournalNotice, PendingOperationAlert, ReceivingBoundary, ReceivingErrorAlert } from './receivingParts';
import type { HoldTarget } from './ResolveHoldDialog';
import { CLOSE_SHORT_EVENTS_BOUND, HISTORY_PAGE_SIZE, REJECT_REASONS, type ReceiptLineView, type ReceiptView, type ReceivingLine } from './receivingTypes';

export interface ReceiptHistoryPanelProps {
  purchaseOrderId: string;
  /** The order's lines, used only to name the lines of close-short events. */
  lines?: readonly ReceivingLine[] | undefined;
  /** Offered for a line with held units to a user who may receive. */
  onDecideHold?: ((target: HoldTarget) => void) | undefined;
  pageSize?: number | undefined;
}

export function ReceiptHistoryPanel(props: ReceiptHistoryPanelProps) {
  return (
    <ReceivingBoundary target={props.purchaseOrderId}>
      <ReceiptHistory {...props} />
    </ReceivingBoundary>
  );
}

type T = (key: string, options?: Record<string, unknown>) => string;

function reasonLabel(reason: string, t: T): string {
  return (REJECT_REASONS as readonly string[]).includes(reason) ? t(`rejectReason.${reason}`) : t('rejectReason.other');
}

function ReceiptHistory({ purchaseOrderId, lines, onDecideHold, pageSize = HISTORY_PAGE_SIZE }: ReceiptHistoryPanelProps) {
  const { t: translate, i18n } = useTranslation('procurementReceiving');
  const t = translate as unknown as T;
  const notify = useNotify();
  const access = useReceivingAccess();
  const [offset, setOffset] = useState(0);
  // A retry the server refused: the pending notice is gone with the request, so the refusal stays here until dismissed.
  const [refusal, setRefusal] = useState<{ error: unknown; what: string } | null>(null);
  const history = useReceiptHistory(purchaseOrderId, { limit: pageSize, offset });
  const ops = useReceivingOperations(purchaseOrderId);
  const page = history.data;
  const language = i18n.language;

  const lineName = (orderLineId: string): string => {
    const known = lines?.find((line) => line.id === orderLineId);
    if (known) return `${known.partNumber} ${known.name}`.trim();
    for (const receipt of page?.items ?? []) {
      const line = receipt.lines.find((candidate) => candidate.purchaseOrderLineId === orderLineId);
      if (line) return `${line.partSku} ${line.partName}`.trim();
    }
    return t('history.unknownLine');
  };

  const handleRetry = (entry: JournalEntry, outcome: SubmitResult) => {
    // Nothing is shown or notified once the owner, generation, lock or rights are no longer those of this screen.
    if (accessKey(readReceivingAccess()) !== accessKey(access)) return;
    if (outcome.status === 'success') {
      setRefusal(null);
      notify.success(t('pending.doneTitle'), t('pending.doneBody'));
    } else if (outcome.status === 'refused') {
      // The server read the request and recorded nothing: say so, in the server's own reason, and do not send anything again.
      setRefusal({ error: outcome.error, what: describeEntry(entry, t) });
      notify.error(t('pending.refusedTitle'), describeReceivingError(outcome.error, translate));
    } else if (outcome.status === 'blocked') {
      notify.error(t('pending.title'), blockedMessage(outcome.reason, t));
    }
  };
  const retryEntry = (entry: JournalEntry) => {
    setRefusal(null);
    void ops.retry(entry).then((outcome) => handleRetry(entry, outcome));
  };

  if (!access.canRead) {
    return <Alert severity="info">{t('history.noReadPermission')}</Alert>;
  }

  return (
    <Stack spacing={2} aria-busy={history.isFetching}>
      <Typography variant="h6" component="h3">
        {t('history.title')}
      </Typography>
      <JournalNotice status={ops.journalStatus} onDiscardDamaged={ops.discardDamaged} onRecheck={ops.recheckStorage} />
      {refusal !== null && (
        <Alert
          severity="error"
          role="alert"
          action={
            <Button color="inherit" size="small" onClick={() => setRefusal(null)}>
              {t('pending.refusedDismiss')}
            </Button>
          }
        >
          <AlertTitle>{t('pending.refusedTitle')}</AlertTitle>
          <Stack spacing={0.5}>
            <span>{t('pending.refusedBody', { what: refusal.what })}</span>
            <span>{describeReceivingError(refusal.error, translate)}</span>
          </Stack>
        </Alert>
      )}
      {ops.entries.map((entry) => (
        <PendingOperationAlert
          key={entry.slot}
          entry={entry}
          error={ops.errorOf(entry.slot)}
          busy={ops.isPending(entry.slot)}
          journalStatus={ops.journalStatus}
          onRetry={() => retryEntry(entry)}
          onDiscard={() => ops.discard(entry)}
        />
      ))}

      {history.isPending && history.fetchStatus !== 'idle' && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
          <CircularProgress size={24} aria-label={t('history.loading')} />
        </Box>
      )}
      {history.isError && !isSessionCancel(history.error) && (
        <Stack spacing={1}>
          <ReceivingErrorAlert error={history.error} />
          <Box>
            <Button size="small" onClick={() => void history.refetch()}>
              {t('history.reload')}
            </Button>
          </Box>
        </Stack>
      )}

      {page && (
        <>
          <Paging
            total={page.pagination.total}
            offset={page.pagination.offset}
            shown={page.items.length}
            hasMore={page.pagination.hasMore}
            busy={history.isFetching}
            onFirst={() => setOffset(0)}
            onPrevious={() => setOffset(Math.max(offset - pageSize, 0))}
            onNext={() => setOffset(offset + pageSize)}
          />
          {page.items.map((receipt) => (
            <ReceiptCard
              key={receipt.id}
              receipt={receipt}
              language={language}
              showCosts={access.canFinancial && page.costsVisible}
              canDecide={access.canReceive && onDecideHold !== undefined}
              onDecide={(line) =>
                onDecideHold?.({
                  receiptLineId: line.id,
                  receiptNumber: receipt.receiptNumber,
                  partNumber: line.partSku,
                  partName: line.partName,
                  quantityHeld: line.quantityHeld,
                })
              }
            />
          ))}

          <Typography variant="h6" component="h3">
            {t('history.closeShort.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {page.closeShort.total === 0
              ? t('history.closeShort.none')
              : page.closeShort.total > page.closeShort.items.length
                ? t('history.closeShort.bounded', { shown: page.closeShort.items.length, total: page.closeShort.total, bound: CLOSE_SHORT_EVENTS_BOUND })
                : t('history.closeShort.count', { count: page.closeShort.total })}
          </Typography>
          {page.closeShort.items.map((event) => (
            <Paper key={event.id} variant="outlined" sx={{ p: 1.5 }}>
              <Typography variant="subtitle2">
                {formatDateTime(event.effectiveAt, language)} · {event.actor.name || t('history.unknownUser')}
              </Typography>
              <Typography variant="body2">{t('history.closeShort.reason', { reason: event.reason })}</Typography>
              {event.lines.map((line) => (
                <Typography key={line.purchaseOrderLineId} variant="body2" color="text.secondary">
                  {t('history.closeShort.line', { part: lineName(line.purchaseOrderLineId), count: line.quantityCancelled })}
                </Typography>
              ))}
            </Paper>
          ))}
        </>
      )}
    </Stack>
  );
}

interface PagingProps {
  total: number;
  offset: number;
  shown: number;
  hasMore: boolean;
  busy: boolean;
  onFirst: () => void;
  onPrevious: () => void;
  onNext: () => void;
}

function Paging({ total, offset, shown, hasMore, busy, onFirst, onPrevious, onNext }: PagingProps) {
  const { t } = useTranslation('procurementReceiving');
  if (total === 0) return <Typography variant="body2">{t('history.empty')}</Typography>;
  const beyond = shown === 0;
  return (
    <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
      <Typography variant="body2" role="status">
        {beyond ? t('history.pastEnd', { total }) : t('history.range', { from: offset + 1, to: offset + shown, total })}
      </Typography>
      {beyond ? (
        <Button size="small" onClick={onFirst} disabled={busy}>
          {t('history.first')}
        </Button>
      ) : (
        <>
          <Button size="small" onClick={onPrevious} disabled={busy || offset === 0}>
            {t('history.previous')}
          </Button>
          <Button size="small" onClick={onNext} disabled={busy || !hasMore}>
            {t('history.next')}
          </Button>
        </>
      )}
    </Stack>
  );
}

interface ReceiptCardProps {
  receipt: ReceiptView;
  language: string;
  showCosts: boolean;
  canDecide: boolean;
  onDecide: (line: ReceiptLineView) => void;
}

function ReceiptCard({ receipt, language, showCosts, canDecide, onDecide }: ReceiptCardProps) {
  const { t } = useTranslation('procurementReceiving');
  return (
    <Paper variant="outlined" sx={{ p: 2 }} component="section" aria-label={t('history.receiptLabel', { number: receipt.receiptNumber })}>
      <Stack spacing={1}>
        <Typography variant="subtitle1">
          {receipt.receiptNumber} · {formatDateTime(receipt.receivedAt, language)}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('history.receivedBy', { name: receipt.receivedBy.name || t('history.unknownUser') })}
          {receipt.deliveryNoteReference !== null && ` · ${t('history.deliveryNote', { reference: receipt.deliveryNoteReference })}`}
        </Typography>
        {receipt.note !== null && <Typography variant="body2">{receipt.note}</Typography>}
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small" aria-label={t('history.linesLabel', { number: receipt.receiptNumber })}>
            <TableHead>
              <TableRow>
                <TableCell>{t('columns.part')}</TableCell>
                <TableCell align="right">{t('columns.arrived')}</TableCell>
                <TableCell align="right">{t('columns.toStock')}</TableCell>
                <TableCell align="right">{t('columns.rejected')}</TableCell>
                <TableCell align="right">{t('columns.heldNow')}</TableCell>
                <TableCell>{t('columns.quality')}</TableCell>
                {showCosts && <TableCell>{t('columns.costs')}</TableCell>}
                {canDecide && <TableCell />}
              </TableRow>
            </TableHead>
            <TableBody>
              {receipt.lines.map((line) => (
                <ReceiptLineRow key={line.id} line={line} language={language} showCosts={showCosts} canDecide={canDecide} onDecide={onDecide} />
              ))}
            </TableBody>
          </Table>
        </Box>
      </Stack>
    </Paper>
  );
}

interface LineRowProps {
  line: ReceiptLineView;
  language: string;
  showCosts: boolean;
  canDecide: boolean;
  onDecide: (line: ReceiptLineView) => void;
}

function ReceiptLineRow({ line, language, showCosts, canDecide, onDecide }: LineRowProps) {
  const { t: translate } = useTranslation('procurementReceiving');
  const t = translate as unknown as T;
  const part = `${line.partSku} ${line.partName}`.trim();
  const unknown = !line.qualityObserved;
  const notRecorded = (
    <span title={t('history.unobservedFigure')} aria-label={t('history.unobservedFigure')}>
      —
    </span>
  );
  return (
    <>
      <TableRow>
        <TableCell component="th" scope="row">
          {part}
        </TableCell>
        <TableCell align="right">{line.quantityArrived}</TableCell>
        <TableCell align="right">{line.quantityAccepted}</TableCell>
        <TableCell align="right">
          {unknown ? (
            notRecorded
          ) : (
            <>
              {line.quantityRejected}
              {line.rejectReason !== null && line.quantityRejected > 0 && ` (${reasonLabel(line.rejectReason, t)})`}
            </>
          )}
        </TableCell>
        <TableCell align="right">
          {unknown ? (
            notRecorded
          ) : (
            <>
              {line.quantityHeld}
              {line.quantityHeldInitial > 0 && line.quantityHeldInitial !== line.quantityHeld && (
                <Typography component="span" variant="caption" color="text.secondary">
                  {` ${t('history.heldInitial', { count: line.quantityHeldInitial })}`}
                </Typography>
              )}
            </>
          )}
        </TableCell>
        <TableCell>
          {unknown ? (
            <Chip size="small" variant="outlined" color="warning" label={t('history.quality.unobserved')} />
          ) : (
            <Chip size="small" color="success" variant="outlined" label={t('history.quality.observed')} title={formatDateTime(line.qualityObservedAt ?? '', language)} />
          )}
        </TableCell>
        {showCosts && <TableCell>{line.costs ? <CostCell costs={line.costs} language={language} /> : '—'}</TableCell>}
        {canDecide && (
          <TableCell align="right">
            {line.quantityHeld > 0 && (
              <Button size="small" variant="outlined" onClick={() => onDecide(line)} aria-label={t('history.decideFor', { part })}>
                {t('history.decide')}
              </Button>
            )}
          </TableCell>
        )}
      </TableRow>
      {(line.note !== null || line.holdResolutions.length > 0) && (
        <TableRow>
          <TableCell colSpan={6 + (showCosts ? 1 : 0) + (canDecide ? 1 : 0)} sx={{ borderBottom: 0, pt: 0 }}>
            {line.note !== null && (
              <Typography variant="caption" display="block" color="text.secondary">
                {t('history.lineNote', { note: line.note })}
              </Typography>
            )}
            {line.holdResolutions.map((decision) => (
              <Typography key={decision.id} variant="caption" display="block" color="text.secondary">
                {t(decision.decision === 'accept' ? 'history.holdAccepted' : 'history.holdRejected', {
                  count: decision.quantity,
                  name: decision.decidedBy.name || t('history.unknownUser'),
                  date: formatDateTime(decision.decidedAt, language),
                })}
                {decision.rejectReason !== null && ` — ${reasonLabel(decision.rejectReason, t)}`}
                {decision.note !== null && ` — ${decision.note}`}
              </Typography>
            ))}
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

function CostCell({ costs, language }: { costs: NonNullable<ReceiptLineView['costs']>; language: string }) {
  const { t: translate } = useTranslation('procurementReceiving');
  const t = translate as unknown as T;
  return (
    <Stack>
      <Typography variant="caption">{t('history.costs.unitPrice', { price: centsTextToEuros(String(costs.unitPriceCents), language) ?? formatCentsForDisplay(costs.unitPriceCents) })}</Typography>
      {costs.discountPercent > 0 && <Typography variant="caption">{t('history.costs.discount', { percent: costs.discountPercent })}</Typography>}
      {costs.layers.map((layer) => {
        const cost = layer.unitCostCents === null ? null : centsTextToEuros(layer.unitCostCents, language);
        return (
          <Typography key={layer.id} variant="caption">
            {t('history.costs.layer', { count: layer.quantity, cost: cost ?? t('history.costs.unknown') })}
          </Typography>
        );
      })}
    </Stack>
  );
}
